import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, FileText, Loader2, Paperclip, Plus, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Combobox } from '../../components/ui/combobox';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Project } from '../../types/lists.types';
import { AiStatus, Product, Yard } from '../../types/stock.types';
import { projectsApi } from '../../utils/lists-api';
import { deliveriesApi, productsApi, stockAiApi, yardsApi } from '../../utils/stock-api';
import GenerateOrderDialog, { GeneratedLine } from './components/GenerateOrderDialog';
import { ApiError } from '../../utils/api';

interface DraftLine { productId: string; quantity: string }

/** Matches the documents endpoint's per-request limit. */
const MAX_SOURCE_DOCS = 10;

/**
 * New Delivery Order (STOCK.md §4).
 *
 * A full page rather than a dialog, matching every other create screen in Idarah: the
 * order-in-progress gets its own URL, so it can be linked, reloaded and backed out
 * of. The list page's "New Delivery" CTA navigates here instead of opening a modal.
 *
 * Routed per yard: `/stock/yards/:yardId/deliveries/new` locks the yard select to that
 * yard when it is open (inside a yard, offering others would let someone create a
 * delivery in a yard they just navigated out of); `/stock/deliveries/new` is the
 * unscoped cross-yard form. When the `:yardId` does not resolve to an open yard — e.g.
 * it is closed — every open yard is offered, because gear can't leave a closed one.
 *
 * Created as Draft with the header and an optional starting material list. Everything
 * here stays editable until it goes for approval, so nothing is required beyond the
 * yard and project — a delivery often starts as "we're sending gear to site X" before
 * anyone knows the truck or the driver.
 *
 * "Generate Order" (§2.5) opens the shared parse-and-review flow. The CTA is hidden
 * rather than disabled when the service is down — a button that cannot work is worse
 * than no button — and the caption switches to the §2.6 manual-entry guidance.
 */
export default function DeliveryOrderFormPage() {
    const navigate = useNavigate();
    const { yardId: yardIdParam } = useParams<{ yardId?: string }>();
    const scopedYardId = yardIdParam ? Number(yardIdParam) : null;
    const ability = useAbility(AbilityContext);
    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.STOCK_DELIVERY_ORDER);

    const [yardId, setYardId] = useState('');
    const [projectId, setProjectId] = useState('');
    const [fleetType, setFleetType] = useState('');
    const [regPlate, setRegPlate] = useState('');
    const [driver, setDriver] = useState('');
    const [shipDate, setShipDate] = useState('');
    const [notes, setNotes] = useState('');
    const [lines, setLines] = useState<DraftLine[]>([{ productId: '', quantity: '' }]);
    const [yards, setYards] = useState<Yard[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [fleetTypes, setFleetTypes] = useState<string[]>([]);
    /**
     * The server's photo rule: fleet type (upper-case) → required load-photo count plus
     * the fallback. Shown at the Fleet Type picker so the count that will be enforced on
     * completion is visible while the truck is being selected (Ops, Sep 2026).
     */
    const [photoRule, setPhotoRule] = useState<
        { byFleet: Record<string, number>; fallback: number }>({ byFleet: {}, fallback: 10 });
    const [saving, setSaving] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
    /** The paste-first list area — handed to Generate Order (§2.5), cleared once added. */
    const [aiText, setAiText] = useState('');
    /** Kept so it can be stored on the order for audit (§2.5). */
    const [rawInputText, setRawInputText] = useState<string | null>(null);
    /** ai_usage_logs row for the parse, sent on create to attribute the spend. */
    const [aiUsageId, setAiUsageId] = useState<number | null>(null);
    /** The sheet file(s) the AI read — stored with the order as evidence (§12.4). */
    const [sheetFiles, setSheetFiles] = useState<File[]>([]);
    /** Source paperwork attached by hand — the storage fallback (§12.4). */
    const [docFiles, setDocFiles] = useState<File[]>([]);
    const docInput = useRef<HTMLInputElement | null>(null);

    // Creating into a closed yard is not a thing: the options are open yards only, and
    // a scoped yard that isn't open simply doesn't narrow them.
    const openYards = Array.isArray(yards) ? yards : [];
    const yardOptions = scopedYardId
        ? openYards.filter((y) => y.id === scopedYardId)
        : openYards;
    // Every project belongs to exactly one yard (FK), so once a yard is chosen only
    // its projects are offered — a project from another yard can't be dispatched from
    // this one. Nothing is offered before a yard is picked.
    const selectedYardId = yardId ? Number(yardId) : null;
    const projectOptions = Array.isArray(projects)
        ? (selectedYardId ? projects.filter((p) => p.yard?.id === selectedYardId) : [])
        : [];
    const productOptions = products.map((p) => ({ label: `${p.name} (${p.sku})`, value: String(p.id) }));
    const projectSelectOptions = projectOptions.map((p) => ({
        label: `${p.projectName} (${p.projectNumber})`,
        value: String(p.id),
    }));

    /** Customer and project manager come from the selected project — read-only. */
    const selectedProject = projectOptions.find((p) => String(p.id) === projectId) ?? null;
    const builder = selectedProject?.customer?.tradingName ?? '';
    const supervisor = selectedProject?.projectManager?.name ?? '';

    /** Photos the selected fleet type demands — from the server's rule, not a local copy. */
    const requiredPhotosFor = (fleet: string) =>
        photoRule.byFleet[fleet.trim().toUpperCase()] ?? photoRule.fallback;

    const listPath = scopedYardId ? `/stock/yards/${scopedYardId}/deliveries` : '/stock/deliveries';
    const detailPath = (id: number) =>
        scopedYardId ? `/stock/yards/${scopedYardId}/deliveries/${id}` : `/stock/deliveries/${id}`;

    useEffect(() => {
        if (!canCreate) return;
        yardsApi.list({ status: 'open' }).then(setYards).catch(() => undefined);
        projectsApi.list({ status: 'active' }).then(setProjects).catch(() => undefined);
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
        deliveriesApi.meta().then((m) => {
            setFleetTypes(m.fleetTypes);
            setPhotoRule({ byFleet: m.photosByFleet ?? {}, fallback: m.minPhotos });
        }).catch(() => undefined);
        // §2.6 — decides whether the CTA or the manual-entry microcopy is shown.
        stockAiApi.status().then(setAiStatus).catch(() =>
            setAiStatus({ available: false, model: '', message: null }));
    }, [canCreate]);

    // Preselect once the options are in, and only while the user hasn't picked: the
    // scoped open yard, or the sole open yard (the common single-yard case).
    useEffect(() => {
        if (yardId || openYards.length === 0) return;
        const preset = scopedYardId && openYards.some((y) => y.id === scopedYardId)
            ? String(scopedYardId)
            : openYards.length === 1 ? String(openYards[0].id) : null;
        if (preset) setYardId(preset);
    }, [yardId, openYards, scopedYardId]);

    // A project must belong to the chosen yard. If the yard changes (or the projects
    // list arrives after the yard and the selection no longer matches), drop the stale
    // project so the read-only customer/PM fields can't show another yard's project.
    useEffect(() => {
        if (!projectId) return;
        const ok = selectedYardId !== null &&
            projects.some((p) => p.yard?.id === selectedYardId && String(p.id) === projectId);
        if (!ok) setProjectId('');
    }, [projectId, projects, selectedYardId]);

    /**
     * Merges reviewed AI lines into the draft. Appends rather than replaces — the user
     * may have already typed some by hand, and silently discarding that would be rude.
     * The sheet files ride along too — they are stored with the order as evidence of
     * what the AI read (§12.4).
     */
    const handleGenerated = (
        generated: GeneratedLine[], raw: string, files: File[], usageId: number | null,
    ) => {
        setRawInputText(raw);
        setAiUsageId(usageId);
        setSheetFiles((prev) => {
            const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
            return [...prev, ...files.filter((f) => !seen.has(`${f.name}:${f.size}`))];
        });
        // The list is now lines in the draft — clear the paste area so a second paste
        // starts fresh instead of silently re-adding the same text.
        setAiText('');
        setLines((prev) => {
            const typed = prev.filter((l) => l.productId && Number(l.quantity) > 0);
            const merged = [...typed];
            for (const g of generated) {
                const existing = merged.findIndex((l) => l.productId === String(g.productId));
                if (existing >= 0) {
                    // Same product from both sources — add the quantities rather than
                    // dropping one silently.
                    merged[existing] = {
                        ...merged[existing],
                        quantity: String(Number(merged[existing].quantity) + g.quantity),
                    };
                } else {
                    merged.push({ productId: String(g.productId), quantity: String(g.quantity) });
                }
            }
            return merged.length ? merged : [{ productId: '', quantity: '' }];
        });
        toast.success(`${generated.length} line(s) added — check them before creating.`);
    };

    const setLine = (index: number, patch: Partial<DraftLine>) =>
        setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

    const handleDocFiles = (picked: FileList | null) => {
        if (!picked?.length) return;
        const incoming = Array.from(picked);
        if (docFiles.length + incoming.length > MAX_SOURCE_DOCS) {
            toast.error(`Up to ${MAX_SOURCE_DOCS} documents — the extra one(s) were ignored.`);
        }
        // Original bytes, no compression — this is the evidentiary copy (§12.4).
        setDocFiles((prev) => [...prev, ...incoming].slice(0, MAX_SOURCE_DOCS));
    };

    const validLines = lines
        .filter((l) => l.productId && Number(l.quantity) > 0)
        .map((l) => ({ productId: Number(l.productId), quantity: Number(l.quantity) }));

    /**
     * Yard, project and ship date are the three things a delivery cannot be without: a
     * yard to leave from, a site to go to, and a date the site is expecting it. Everything
     * else on this form can arrive later — the truck details are filled in as the run is
     * allocated, which is why they live in their own card. The server enforces the same
     * three (POST /api/stock/deliveries).
     */
    const canSubmit = Boolean(yardId && projectId && shipDate) && !saving;

    /**
     * Synchronous re-entry guard for the create.
     *
     * `saving` disables the button, but that is a render away: a second click landing
     * while React is still committing would post the order twice, and a duplicate delivery
     * is not something the yard can undo (numbers are never reused, §2.2). A ref is set in
     * the same tick as the first click, so the second cannot get through.
     */
    const submittingRef = useRef(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit || submittingRef.current) return;
        submittingRef.current = true;
        try {
            setSaving(true);
            const order = await deliveriesApi.create({
                yardId: Number(yardId),
                projectId: Number(projectId),
                builder: builder.trim() || null,
                supervisor: supervisor.trim() || null,
                fleetType: fleetType || null,
                regPlate: regPlate.trim() || null,
                driver: driver.trim() || null,
                shipDate,
                notes: notes.trim() || null,
                lines: validLines.length ? validLines : undefined,
                // Only set when the list came through the parser — §2.5 wants the raw
                // text kept alongside what was made of it, and the ledger row linked
                // to the order it produced.
                rawInputText,
                aiUsageId,
            });
            // §12.4 — the order has to exist before documents can hang off it, so the
            // paperwork uploads right after the create. A failure here must not lose
            // the order: warn and carry on; the detail page can take them later.
            try {
                if (sheetFiles.length) await deliveriesApi.uploadDocuments(order.id, sheetFiles, 'ai_parse');
                if (docFiles.length) await deliveriesApi.uploadDocuments(order.id, docFiles, 'upload');
            } catch {
                toast.error(
                    `${order.orderNumber} was created, but the document(s) failed to upload ` +
                    `— attach them again from the order page.`
                );
            }
            toast.success(`${order.orderNumber} created`);
            navigate(detailPath(order.id));
        } catch (err: unknown) {
            const message = err instanceof ApiError
                ? err.data?.message ?? err.message
                : err instanceof Error ? err.message : 'Failed to create delivery';
            toast.error(message);
            // The order was NOT created, so let them try again.
            submittingRef.current = false;
        } finally {
            setSaving(false);
        }
    };

    if (!canCreate) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={() => navigate(listPath)}
                        className="mb-2 -ml-2">
                    <ArrowLeft size={16} className="mr-1.5" />Deliveries
                </Button>
                <h1 className="text-2xl font-bold">New Delivery</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Created as a draft. The ship date goes on now; the rest of the truck
                    details can follow later, and the list is submitted for approval when
                    it is right.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-5">
                        <h2 className="text-base font-semibold">Details</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="d-yard">Yard *</Label>
                                <Select value={yardId} onValueChange={setYardId}>
                                    <SelectTrigger id="d-yard">
                                        <SelectValue placeholder={yardOptions.length ? 'Select a yard' : 'No open yards'} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {yardOptions.map((y) => (
                                            <SelectItem key={y.id} value={String(y.id)}>
                                                {y.yardName} ({y.yardCode})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Project *</Label>
                                <Combobox
                                    options={projectSelectOptions}
                                    value={projectId || undefined}
                                    onChange={(v) => setProjectId(v || '')}
                                    placeholder={!yardId
                                        ? 'Select a yard first'
                                        : (projectOptions.length ? 'Select a project' : 'No projects for this yard')}
                                    searchPlaceholder="Search projects..."
                                    emptyMessage="No projects found."
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="d-customer">Customer</Label>
                                <Input id="d-customer" value={builder} disabled
                                       placeholder="From the selected project" />
                            </div>
                            <div>
                                <Label htmlFor="d-pm">Project Manager</Label>
                                <Input id="d-pm" value={supervisor} disabled
                                       placeholder="From the selected project" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <h2 className="text-base font-semibold">Truck details</h2>
                        <p className="text-xs text-muted-foreground -mt-2">
                            Optional, apart from the ship date — the yard and the site both work
                            off that, so it is asked for up front. The truck itself can still be
                            allocated later.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <Label htmlFor="d-fleet">Fleet Type</Label>
                                <Select value={fleetType} onValueChange={setFleetType}>
                                    <SelectTrigger id="d-fleet"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                        {fleetTypes.map((f) => (
                                            <SelectItem key={f} value={f}>{f}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {/* Ops, Sep 2026: the load-photo floor follows the truck, so
                                    say what choosing this type commits the crew to. */}
                                <p className="text-xs text-muted-foreground mt-1">
                                    Load photos required: {requiredPhotosFor(fleetType)}
                                </p>
                            </div>
                            <div>
                                <Label htmlFor="d-rego">Rego / Plate</Label>
                                <Input id="d-rego" value={regPlate} onChange={(e) => setRegPlate(e.target.value)} />
                            </div>
                            <div>
                                <Label htmlFor="d-driver">Driver</Label>
                                <Input id="d-driver" value={driver} onChange={(e) => setDriver(e.target.value)} />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="d-ship">Ship Date *</Label>
                            <Input id="d-ship" type="date" value={shipDate}
                                   onChange={(e) => setShipDate(e.target.value)} className="max-w-xs" />
                        </div>
                    </CardContent>
                </Card>

                {/* Material list */}
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <div>
                            <h2 className="text-base font-semibold">Material list</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Optional — add the lines now, or after creating the draft.
                            </p>
                        </div>

                        {/* §2.5/§2.6 — two ways to fill the list, and they mix: paste it and
                            Generate reads it into the rows below, or add the rows by hand.
                            The manual picker is ALWAYS available (§2.6) — the AI box is the
                            convenience, not the only door in. */}
                        {aiStatus !== null && !aiStatus.available ? (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                Generate with AI is currently down — add the products and
                                quantities manually below. You can also add lines after
                                creating the draft.
                            </p>
                        ) : (
                            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
                                <p className="text-xs font-medium text-blue-900">
                                    Paste the list as it was written — slang and shorthand are fine.
                                </p>
                                <Textarea
                                    value={aiText}
                                    onChange={(e) => setAiText(e.target.value)}
                                    rows={4}
                                    placeholder={'40 standards 3.0m\n20 8ft ledgers\na dozen 2.4m boards\n15 swivels'}
                                    className="font-mono text-sm bg-white"
                                />
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs text-muted-foreground">
                                        AI turns it into lines you review before they're added.
                                    </p>
                                    <Button type="button" size="sm" onClick={() => setAiOpen(true)}
                                            disabled={!aiText.trim()}>
                                        <Sparkles size={14} className="mr-1.5" />Generate Order
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Please enter the gear list manually if the 'Generate with AI' feature is
                                    unavailable or not working as expected.
                                </p>
                            </div>
                        )}

                        {/* Manual picker — always available; reviewed AI lines land here too. */}
                        <p className="text-xs text-muted-foreground">or add lines manually</p>
                        <div className="space-y-2">
                            {lines.map((line, index) => (
                                <div key={index} className="grid grid-cols-[2fr_1fr_auto] gap-2">
                                    <Combobox
                                        options={productOptions}
                                        value={line.productId || undefined}
                                        onChange={(v) => setLine(index, { productId: v || '' })}
                                        placeholder="Select a product"
                                        searchPlaceholder="Search products..."
                                        emptyMessage="No products found."
                                    />
                                    {/* Whole numbers only: a load count is a count of
                                        items, and `inputMode="numeric"` gives phones the
                                        keypad without a decimal point. The implicit step of
                                        1 makes a typed decimal an invalid control, so the
                                        browser refuses the submit rather than silently
                                        rounding it — the server rejects the same value. */}
                                    <Input type="number" inputMode="numeric" min="1"
                                           placeholder="Qty"
                                           value={line.quantity}
                                           onChange={(e) => setLine(index, { quantity: e.target.value })} />
                                    <Button type="button" variant="ghost" size="icon" aria-label="Remove line"
                                            disabled={lines.length === 1}
                                            onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}>
                                        <X size={16} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <Button type="button" variant="outline" size="sm"
                                onClick={() => setLines((prev) => [...prev, { productId: '', quantity: '' }])}>
                            <Plus size={14} className="mr-1.5" />Add line
                        </Button>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4 md:p-6 space-y-2">
                        <Label htmlFor="d-notes">Notes</Label>
                        <Textarea id="d-notes" rows={2} value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Tools or notes for the yard" />
                    </CardContent>
                </Card>

                {/* §12.4 — source paperwork: the storage fallback, plus the sheet the AI
                    read (tagged). Stored with the order as the source of record; the
                    original bytes go up, no compression. */}
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <div>
                            <h2 className="text-base font-semibold">
                                Source documents <span className="font-normal text-muted-foreground">(optional)</span>
                            </h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Attach the paperwork behind this delivery — the order confirmation,
                                a scanned docket, PDFs or photos — so the original is never lost.
                            </p>
                        </div>
                        <input
                            ref={docInput}
                            type="file"
                            accept="application/pdf,image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                handleDocFiles(e.target.files);
                                // Reset so picking the same file again still fires onChange.
                                e.target.value = '';
                            }}
                        />
                        <Button type="button" variant="outline" size="sm"
                                onClick={() => docInput.current?.click()}>
                            <Paperclip size={14} className="mr-1.5" />Attach documents
                        </Button>
                        {(sheetFiles.length > 0 || docFiles.length > 0) && (
                            <div className="divide-y divide-border border border-border rounded-lg bg-white">
                                {([
                                    ...sheetFiles.map((f, i) => ({
                                        file: f, tag: 'AI sheet', key: `sheet-${i}`,
                                        remove: () => setSheetFiles((prev) =>
                                            prev.filter((_, j) => j !== i)),
                                    })),
                                    ...docFiles.map((f, i) => ({
                                        file: f, tag: null as string | null, key: `doc-${i}`,
                                        remove: () => setDocFiles((prev) =>
                                            prev.filter((_, j) => j !== i)),
                                    })),
                                ]).map(({ file, tag, key, remove }) => (
                                    <div key={key} className="flex items-center gap-3 p-2">
                                        <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                            <FileText size={16} className="text-muted-foreground" />
                                        </span>
                                        <span className="text-sm truncate flex-1">{file.name}</span>
                                        {tag && (
                                            <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded px-1.5 py-0.5 shrink-0">
                                                {tag}
                                            </span>
                                        )}
                                        <Button type="button" variant="ghost" size="icon"
                                                aria-label="Remove document" onClick={remove}>
                                            <X size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => navigate(listPath)}
                            className="w-full md:w-auto">
                        Cancel
                    </Button>
                    <Button type="submit" disabled={!canSubmit} className="w-full md:w-auto">
                        {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                        Create Draft
                    </Button>
                </div>
            </form>

            {/* Outside the form — its review-step buttons must not submit the order. */}
            <GenerateOrderDialog
                open={aiOpen}
                onOpenChange={setAiOpen}
                products={products}
                onConfirm={handleGenerated}
                initialText={aiText}
            />
        </div>
    );
}
